package com.evoapps.evoshift.viewmodels

import com.evoapps.evoshift.domain.usecase.billing.ObservePlanActiveUseCase
import com.evoapps.evoshift.domain.model.Capability
import com.evoapps.evoshift.domain.model.capabilitiesAt
import kotlinx.coroutines.ExperimentalCoroutinesApi
import kotlinx.coroutines.Job
import com.evoapps.evoshift.domain.repository.RosterRepository
import com.evoapps.evoshift.ui.util.UiText
import com.evoapps.evoshift.ui.util.toUiText
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.requests_resolve_failed
import evoshift.composeapp.generated.resources.requests_undo_changed
import evoshift.composeapp.generated.resources.requests_undone
import com.evoapps.evoshift.domain.usecase.requests.DecisionChangedException
import com.evoapps.evoshift.domain.usecase.requests.ObservePendingRequestsUseCase
import com.evoapps.evoshift.domain.usecase.requests.UndoAbsenceDecisionUseCase
import com.evoapps.evoshift.domain.usecase.requests.UndoSwapApprovalUseCase
import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.evoapps.evoshift.domain.model.Absence
import com.evoapps.evoshift.domain.model.AbsenceStatus
import com.evoapps.evoshift.domain.model.UserPermission
import com.evoapps.evoshift.domain.repository.AbsenceRepository
import com.evoapps.evoshift.domain.usecase.auth.GetCurrentUserUseCase
import com.evoapps.evoshift.session.WorkplaceSessionManager
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.catch
import kotlinx.coroutines.flow.collectLatest
import kotlinx.coroutines.flow.first
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import com.evoapps.evoshift.domain.usecase.marketplace.ApproveSwapRequestUseCase
import com.evoapps.evoshift.domain.usecase.marketplace.RejectSwapRequestUseCase
import com.evoapps.evoshift.domain.usecase.ResolveAbsenceConflictsUseCase
import com.evoapps.evoshift.domain.model.Shift
import com.evoapps.evoshift.domain.repository.ShiftRepository
import com.evoapps.evoshift.domain.util.AppClock.now
import com.evoapps.evoshift.domain.model.WorkRequest
import com.evoapps.evoshift.domain.model.WorkRequestKind
import com.evoapps.evoshift.domain.repository.WorkRequestRepository
import com.evoapps.evoshift.domain.requests.LimitCheck
import com.evoapps.evoshift.domain.usecase.requests.CheckApprovalLimitUseCase
import com.evoapps.evoshift.domain.usecase.requests.DecideWorkRequestUseCase
import com.evoapps.evoshift.domain.usecase.requests.AbsenceDecision
import com.evoapps.evoshift.domain.usecase.requests.DecideAbsenceUseCase
import com.evoapps.evoshift.domain.usecase.requests.ResolveConflictsFailedException

data class RequestsUiState(
    val isLoading: Boolean = false,
    val pendingAbsences: List<AbsenceWithConflicts> = emptyList(),
    val pendingSwaps: List<Shift> = emptyList(),
    val otherRequests: List<Any> = emptyList(), // Can be Absence or Shift
    /** What the person may do at this workplace: deciding requests needs APPROVE_REQUESTS. */
    val capabilities: Set<Capability> = emptySet(),
    val workplaceId: String? = null,
    /** SH-163: the team's names by uid, so an absence request says whose it is. */
    val names: Map<String, String> = emptyMap(),
    /** SH-164: the last one-tap approval, offered for undo in the snackbar ("Approved." / Undo). */
    val undo: DecisionUndo? = null,
    /** SH-164: a short line once an Undo went through. */
    val notice: UiText? = null,
    val error: UiText? = null,
    /** SH-168: the requests could not be read (nothing shown): Try again instead of "no requests". */
    val loadError: UiText? = null,
    /** SH-168: a pull-to-refresh is reading them again. */
    val isRefreshing: Boolean = false,
    /** SH-223: remote work, work events and overtime waiting for a decision (an event with the shifts it clashes with). */
    val pendingWork: List<WorkRequestWithConflicts> = emptyList(),
    /** SH-223: the decided ones (the approved are listed). */
    val decidedWork: List<WorkRequest> = emptyList(),
    /** SH-223: an approval over a yearly limit, waiting for the manager's note. */
    val overLimit: OverLimitApproval? = null
)

/** SH-223: a work request and the shifts of the person it overlaps (a warning: they stay). */
data class WorkRequestWithConflicts(val request: WorkRequest, val conflicts: List<Shift> = emptyList())

/** SH-223: [absence] goes over its limit ([check]): approved only with a note. */
data class OverLimitApproval(val absence: Absence, val check: LimitCheck.Exceeds)

/**
 * SH-164: an approval made in one tap (nothing at stake: no shift loses its person), kept until
 * its snackbar is gone; Undo puts the request back as it was ([before]).
 */
sealed interface DecisionUndo {
    data class AbsenceApproval(val before: Absence) : DecisionUndo
    data class SwapApproval(val before: Shift) : DecisionUndo
}

/** SH-164: the longest reason a manager can give for a rejection. */
const val REJECTION_REASON_MAX_LENGTH = 500

/** The reason as stored: trimmed, at most [REJECTION_REASON_MAX_LENGTH], null when blank. */
internal fun String?.asRejectionReason(): String? = this?.trim()?.take(REJECTION_REASON_MAX_LENGTH)?.takeIf { it.isNotEmpty() }

data class AbsenceWithConflicts(
    val absence: Absence,
    val conflicts: List<Shift> = emptyList()
)

sealed interface RequestsEvent {
    data object Refresh : RequestsEvent
    /** SH-223: [overrideNote] approves over a yearly limit (asked for when the limit is exceeded). */
    data class ApproveAbsence(val absence: Absence, val overrideNote: String? = null) : RequestsEvent
    /** SH-223: the over-limit question closed without approving. */
    data object DismissOverLimit : RequestsEvent
    data class ApproveWork(val request: WorkRequest) : RequestsEvent
    data class RejectWork(val request: WorkRequest, val reason: String? = null) : RequestsEvent
    /** SH-164: [reason] (optional) is stored on the request and shown to the employee. */
    data class RejectAbsence(val absence: Absence, val reason: String? = null) : RequestsEvent
    data class ApproveSwap(val shiftId: String) : RequestsEvent
    /** SH-164: [reason] (optional) becomes the swap's manager note, shown to the employee. */
    data class RejectSwap(val shiftId: String, val reason: String? = null) : RequestsEvent
    /** SH-164: the snackbar's Undo of the last one-tap approval. */
    data object UndoDecision : RequestsEvent
    /** SH-164: the approval's snackbar is gone: the decision stays. */
    data object DismissUndo : RequestsEvent
    data object ClearNotice : RequestsEvent
    data object ClearError : RequestsEvent
}

@OptIn(ExperimentalCoroutinesApi::class)
class RequestsViewModel(
    private val absenceRepository: AbsenceRepository,
    private val shiftRepository: ShiftRepository,
    private val rosterRepository: RosterRepository,
    private val getCurrentUserUseCase: GetCurrentUserUseCase,
    private val resolveAbsenceConflictsUseCase: ResolveAbsenceConflictsUseCase,
    private val approveSwapRequestUseCase: ApproveSwapRequestUseCase,
    private val rejectSwapRequestUseCase: RejectSwapRequestUseCase,
    private val sessionManager: WorkplaceSessionManager,
    private val observePlanActive: ObservePlanActiveUseCase? = null,
    /** SH-163: the team's names for the absence requests; without it they show no name. */
    private val getAssignableEmployees: com.evoapps.evoshift.domain.usecase.user.GetAssignableEmployeesUseCase? = null,
    /** SH-164: the same requests the badges count. */
    private val observePendingRequests: ObservePendingRequestsUseCase = ObservePendingRequestsUseCase(rosterRepository, shiftRepository, absenceRepository),
    private val undoAbsenceDecision: UndoAbsenceDecisionUseCase = UndoAbsenceDecisionUseCase(absenceRepository),
    private val undoSwapApproval: UndoSwapApprovalUseCase = UndoSwapApprovalUseCase(shiftRepository),
    /** SH-223: work requests; without it none are listed. */
    private val workRequestRepository: WorkRequestRepository? = null,
    private val decideWorkRequest: DecideWorkRequestUseCase? = workRequestRepository?.let { DecideWorkRequestUseCase(it) },
    /** SH-223: the yearly limits checked again before an approval; without it none are. */
    private val checkApprovalLimit: CheckApprovalLimitUseCase? = null,
    private val decideAbsence: DecideAbsenceUseCase = DecideAbsenceUseCase(absenceRepository, resolveAbsenceConflictsUseCase, checkApprovalLimit)
) : ViewModel() {

    private var swapsJob: Job? = null
    /** SH-164: the decision being saved; an Undo waits for it, so it never runs before the save. */
    private var decisionJob: Job? = null

    private val _uiState = MutableStateFlow(RequestsUiState())
    val uiState: StateFlow<RequestsUiState> = _uiState.asStateFlow()

    init {
        observeSession()
    }

    private fun observeSession() {
        viewModelScope.launch {
            sessionManager.currentWorkplaceId.collectLatest { workplaceId ->
                val user = getCurrentUserUseCase().first()
                if (user != null && workplaceId != null) {
                    val capabilities = user.capabilitiesAt(workplaceId, observePlanActive.activeNow(workplaceId))
                    _uiState.update { state ->
                        // Another workplace's requests must not stay on screen while these load.
                        if (state.workplaceId == workplaceId) state.copy(capabilities = capabilities)
                        else RequestsUiState(workplaceId = workplaceId, capabilities = capabilities)
                    }

                    loadRequests()
                }
            }
        }
    }

    fun onEvent(event: RequestsEvent) {
        when (event) {
            RequestsEvent.Refresh -> {
                _uiState.update { it.copy(isRefreshing = true, loadError = null) }
                loadRequests()
            }
            is RequestsEvent.ApproveAbsence -> if (mayDecide()) {
                _uiState.update { it.copy(overLimit = null) }
                updateAbsenceStatus(event.absence, AbsenceStatus.APPROVED, overrideNote = event.overrideNote?.trim()?.takeIf { it.isNotEmpty() })
            }
            RequestsEvent.DismissOverLimit -> _uiState.update { it.copy(overLimit = null) }
            is RequestsEvent.ApproveWork -> if (mayDecide()) decideWork(event.request, AbsenceStatus.APPROVED)
            is RequestsEvent.RejectWork -> if (mayDecide()) decideWork(event.request, AbsenceStatus.REJECTED, event.reason.asRejectionReason())
            is RequestsEvent.RejectAbsence -> if (mayDecide()) updateAbsenceStatus(event.absence, AbsenceStatus.REJECTED, event.reason.asRejectionReason())
            is RequestsEvent.ApproveSwap -> if (mayDecide()) approveSwap(event.shiftId)
            is RequestsEvent.RejectSwap -> if (mayDecide()) rejectSwap(event.shiftId, event.reason.asRejectionReason())
            RequestsEvent.UndoDecision -> if (mayDecide()) undoDecision()
            RequestsEvent.DismissUndo -> _uiState.update { it.copy(undo = null) }
            RequestsEvent.ClearNotice -> _uiState.update { it.copy(notice = null) }
            RequestsEvent.ClearError -> _uiState.update { it.copy(error = null) }
        }
    }

    /** Deciding: not once the workplace's plan ended (read-only, SH-65). */
    private fun mayDecide(): Boolean = _uiState.value.capabilities.shows(Gate.DECIDE_REQUESTS)

    private fun loadRequests() {
        val workplaceId = _uiState.value.workplaceId ?: return
        // The workplace's requests are the managers' to read and decide.
        if (!_uiState.value.capabilities.shows(Gate.TEAM_REQUESTS)) {
            swapsJob?.cancel()
            _uiState.update {
                it.copy(isLoading = false, isRefreshing = false, pendingSwaps = emptyList(), pendingAbsences = emptyList(), otherRequests = emptyList(),
                    pendingWork = emptyList(), decidedWork = emptyList())
            }
            return
        }
        viewModelScope.launch {
            _uiState.update { it.copy(isLoading = it.hasNoRequests()) }

            // Swap requests live on shifts, which are queried per roster (Firestore rules prove
            // access through the roster id): watch every roster of the workplace and keep only the
            // requests a manager has to decide on.
            swapsJob?.cancel()
            // SH-164: the same requests the badges count (ObservePendingRequestsUseCase).
            swapsJob = launch {
                observePendingRequests.swaps(workplaceId)
                    // SH-168: a failed listener: Try again when nothing is shown, else a snackbar.
                    .catch { e -> _uiState.update { it.failedToLoad(e.toUiText()) } }
                    .collect { needsDecision ->
                        _uiState.update { it.copy(pendingSwaps = needsDecision) }
                    }
            }
            
            // Observe finalized swaps (recently approved)
            launch {
                // Here we fetch shifts that were recently modified and had a swap request cleared
                // For simplicity in this UI, we might want to observe shifts that are NOT pending 
                // but were part of a swap. Since "finalized" means metadata is cleared, 
                // we'll need a way to identify them. For now, let's just focus on Absences 
                // and potentially "Marketplace" shifts that are assigned.
                
                // Let's refine the approach: we'll use 'otherRequests' for both
                // Approved/Rejected Absences and potentially "Recently Swapped" shifts if we had a way to track them.
            }

            loadAbsences(workplaceId)
        }
    }

    /** Reads the absence requests and the shifts each one clashes with. Never shows a spinner itself. */
    private suspend fun loadAbsences(workplaceId: String) {
        val absencesResult = absenceRepository.getAbsencesForWorkplace(workplaceId)
        val names = getAssignableEmployees?.invoke(workplaceId)?.getOrNull()?.associate { it.uid to it.displayName }
        absencesResult.fold(
            onSuccess = { absences ->
                val pendingAbsences = absences.filter { it.status == AbsenceStatus.PENDING }
                val withConflicts = pendingAbsences.map { absence ->
                    val conflicts = shiftRepository.getUserShiftsInWorkplace(
                        workplaceId = workplaceId,
                        userId = absence.userId,
                        startDate = absence.startDate,
                        endDate = absence.endDate
                    ).getOrDefault(emptyList())
                        // SH-223: part of a day frees only the shifts it overlaps.
                        .filter { ResolveAbsenceConflictsUseCase.overlaps(it, absence.startDate, absence.startTime, absence.endTime) }
                    AbsenceWithConflicts(absence, conflicts)
                }

                _uiState.update { it.copy(
                    isLoading = false,
                    isRefreshing = false,
                    loadError = null,
                    pendingAbsences = withConflicts,
                    names = names ?: it.names,
                    otherRequests = absences.filter { it.status != AbsenceStatus.PENDING }
                ) }
            },
            onFailure = { error ->
                _uiState.update { it.failedToLoad(error.toUiText()) }
            }
        )
        loadWork(workplaceId)
    }

    /**
     * SH-223: the workplace's work requests: the pending ones (a work event with the shifts it
     * clashes with, which stay), and the approved ones.
     */
    private suspend fun loadWork(workplaceId: String) {
        val repository = workRequestRepository ?: return
        val all = repository.forWorkplace(workplaceId).getOrNull() ?: return
        val pending = all.filter { it.status == AbsenceStatus.PENDING }.sortedBy { it.startDate }.map { request ->
            val conflicts = if (request.kind != WorkRequestKind.WORK_EVENT) emptyList()
            else shiftRepository.getUserShiftsInWorkplace(workplaceId, request.userId, request.startDate, request.endDate).getOrDefault(emptyList())
                .filter { ResolveAbsenceConflictsUseCase.overlaps(it, it.date.ifEmpty { request.startDate }, request.startTime, request.endTime) }
            WorkRequestWithConflicts(request, conflicts)
        }
        _uiState.update { it.copy(pendingWork = pending, decidedWork = all.filter { r -> r.status == AbsenceStatus.APPROVED }) }
    }

    /** SH-223: approves or rejects a work request; the person's shifts stay as they are. */
    private fun decideWork(request: WorkRequest, status: AbsenceStatus, reason: String? = null) {
        val decide = decideWorkRequest ?: return
        val before = _uiState.value
        _uiState.update { state ->
            state.copy(
                pendingWork = state.pendingWork.filter { it.request.id != request.id },
                decidedWork = if (status == AbsenceStatus.APPROVED) state.decidedWork + request.copy(status = status) else state.decidedWork
            )
        }
        decisionJob = viewModelScope.launch {
            val user = getCurrentUserUseCase().first() ?: return@launch _uiState.update { it.copy(pendingWork = before.pendingWork, decidedWork = before.decidedWork) }
            decide(request, status, user.uid, user.displayName, reason).onFailure { error ->
                _uiState.update { it.copy(pendingWork = before.pendingWork, decidedWork = before.decidedWork, error = error.toUiText()) }
            }
        }
    }

    private fun RequestsUiState.hasNoRequests() =
        pendingAbsences.isEmpty() && pendingSwaps.isEmpty() && otherRequests.isEmpty() && pendingWork.isEmpty() && decidedWork.isEmpty()

    /** SH-168: nothing on screen yet: a load error (Try again); over requests already shown, a snackbar. */
    private fun RequestsUiState.failedToLoad(message: UiText) =
        if (hasNoRequests()) copy(isLoading = false, isRefreshing = false, loadError = message)
        else copy(isLoading = false, isRefreshing = false, error = message)

    /** SH-164: approving a swap frees no one's other shifts: one tap, with Undo. */
    private fun approveSwap(shiftId: String) = decideSwap(shiftId, offerUndo = true) { approveSwapRequestUseCase(shiftId) }

    private fun rejectSwap(shiftId: String, reason: String?) = decideSwap(shiftId, offerUndo = false) { rejectSwapRequestUseCase(shiftId, reason) }

    /**
     * The decided request leaves the list at once; the swaps listener confirms it. A failure puts
     * it back into the current list (the listener may have emitted meanwhile), not an old copy.
     * [offerUndo]: the snackbar offers Undo at once (SH-164), withdrawn when the save fails.
     */
    private fun decideSwap(shiftId: String, offerUndo: Boolean, decide: suspend () -> Result<Unit>) {
        val decided = _uiState.value.pendingSwaps.find { it.id == shiftId }
        _uiState.update { state ->
            state.copy(
                pendingSwaps = state.pendingSwaps.filter { it.id != shiftId },
                undo = if (offerUndo && decided != null) DecisionUndo.SwapApproval(decided) else state.undo
            )
        }
        decisionJob = viewModelScope.launch {
            decide().onFailure { error ->
                _uiState.update { state ->
                    val restored = if (decided == null || state.pendingSwaps.any { it.id == shiftId }) state.pendingSwaps
                    else (state.pendingSwaps + decided).sortedBy { it.plannedTime.start }
                    state.copy(pendingSwaps = restored, undo = state.undo.takeUnless { it.isOf(shiftId) }, error = error.toUiText())
                }
            }
        }
    }

    private fun DecisionUndo?.isOf(id: String): Boolean = when (this) {
        is DecisionUndo.SwapApproval -> before.id == id
        is DecisionUndo.AbsenceApproval -> before.id == id
        null -> false
    }

    /**
     * SH-164: the snackbar's Undo: the request is back on the list at once and waits for a
     * decision again; it waits for the approval's own save first. When the request changed since
     * (a colleague took the shift), nothing is overwritten and the screen says so.
     */
    private fun undoDecision() {
        val undo = _uiState.value.undo ?: return
        _uiState.update { it.copy(undo = null) }
        val pending = decisionJob
        when (undo) {
            is DecisionUndo.SwapApproval -> {
                val before = undo.before
                _uiState.update { state ->
                    if (state.pendingSwaps.any { it.id == before.id }) state
                    else state.copy(pendingSwaps = (state.pendingSwaps + before).sortedBy { it.plannedTime.start })
                }
                decisionJob = viewModelScope.launch {
                    pending?.join()
                    undoSwapApproval(before).fold(
                        onSuccess = { _uiState.update { it.copy(notice = UiText.Resource(Res.string.requests_undone)) } },
                        onFailure = { error ->
                            // The listener has nothing new to say: take the shown copy back out.
                            _uiState.update { state -> state.copy(pendingSwaps = state.pendingSwaps.filterNot { it === before }, error = undoError(error)) }
                        }
                    )
                }
            }
            is DecisionUndo.AbsenceApproval -> {
                val before = undo.before
                _uiState.update { state ->
                    state.copy(
                        pendingAbsences = if (state.pendingAbsences.any { it.absence.id == before.id }) state.pendingAbsences
                        else state.pendingAbsences + AbsenceWithConflicts(before),
                        otherRequests = state.otherRequests.filterNot { it is Absence && it.id == before.id }
                    )
                }
                decisionJob = viewModelScope.launch {
                    pending?.join()
                    undoAbsenceDecision(before).fold(
                        onSuccess = { _uiState.update { it.copy(notice = UiText.Resource(Res.string.requests_undone)) } },
                        onFailure = { error -> _uiState.update { it.copy(error = undoError(error)) } }
                    )
                    // Quietly, as after a decision: the list as stored.
                    _uiState.value.workplaceId?.let { loadAbsences(it) }
                }
            }
        }
    }

    private fun undoError(error: Throwable): UiText =
        if (error is DecisionChangedException) UiText.Resource(Res.string.requests_undo_changed) else error.toUiText()

    private fun updateAbsenceStatus(absence: Absence, status: AbsenceStatus, rejectionReason: String? = null, overrideNote: String? = null) {
        val before = _uiState.value
        // SH-164: an approval that frees no shift is one tap, with Undo (the snackbar's).
        val offerUndo = status == AbsenceStatus.APPROVED &&
            before.pendingAbsences.firstOrNull { it.absence.id == absence.id }?.conflicts?.isEmpty() == true
        // Decided at once: the request leaves "pending" (an approval shows under "accepted").
        _uiState.update { state ->
            state.copy(
                pendingAbsences = state.pendingAbsences.filter { it.absence.id != absence.id },
                otherRequests = state.otherRequests + absence.copy(status = status),
                undo = if (offerUndo) DecisionUndo.AbsenceApproval(absence) else state.undo
            )
        }
        decisionJob = viewModelScope.launch {
            val user = getCurrentUserUseCase().first() ?: return@launch rollBack(before)
            val workplaceId = _uiState.value.workplaceId ?: absence.workplaceId
            if (status == AbsenceStatus.APPROVED && workplaceId == null) return@launch rollBack(before)
            // SH-224: SH-223's decision (the limits again, the shifts freed, the save), shared with the timeline.
            decideAbsence(absence, status, user.uid, user.displayName, workplaceId, rejectionReason, overrideNote).fold(
                onSuccess = { decision ->
                    when (decision) {
                        is AbsenceDecision.OverLimit -> {
                            rollBack(before)
                            _uiState.update { it.copy(overLimit = OverLimitApproval(absence, decision.check), undo = null) }
                        }
                        // Quietly: approving may have unassigned shifts, which changes other requests' conflicts.
                        is AbsenceDecision.Decided -> _uiState.value.workplaceId?.let { loadAbsences(it) }
                    }
                },
                onFailure = { error ->
                    val cause = (error as? ResolveConflictsFailedException)?.cause
                    rollBack(before, if (cause != null) UiText.Resource(Res.string.requests_resolve_failed, cause.toUiText()) else error.toUiText())
                }
            )
        }
    }

    /** Undoes an optimistic absence decision whose save failed, and says why (when there is a why). */
    private fun rollBack(before: RequestsUiState, error: UiText? = null) {
        _uiState.update {
            it.copy(
                pendingAbsences = before.pendingAbsences,
                otherRequests = before.otherRequests,
                // A failed approval has nothing to undo.
                undo = it.undo.takeUnless { undo -> undo is DecisionUndo.AbsenceApproval && before.pendingAbsences.any { p -> p.absence.id == undo.before.id } },
                error = error ?: it.error
            )
        }
    }
}
